import { useCallback, useEffect, useState } from 'react';
import { fetchPuzzle } from '../api/puzzleClient';

/**
 * Load a puzzle snapshot.
 *
 * The dependency list is deliberately just [puzzleId, attempt]. The old
 * generator was driven by a `seedRandom` closure rebuilt on every render, so
 * every effect that touched it re-ran constantly; carrying that pattern into a
 * fetch would mean an infinite refetch loop.
 *
 * @param {string|number|null} puzzleId  null for today's puzzle.
 */
const usePuzzle = (puzzleId = null) => {
  const [state, setState] = useState({ status: 'loading', puzzle: null, error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading', puzzle: null, error: null });

    fetchPuzzle(puzzleId, { signal: controller.signal })
      .then((puzzle) => {
        if (active) setState({ status: 'ready', puzzle, error: null });
      })
      .catch((error) => {
        if (!active || error.name === 'AbortError') return;
        setState({ status: 'error', puzzle: null, error });
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [puzzleId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { ...state, retry };
};

export default usePuzzle;
